import { MedewerkersOpStation, usePatchMedewerkersOpStation, usePostMedewerkersOpStationAanvraag } from "@/entities/medewerkers-op-station";
import { useGetStationDisplayName } from "@/entities/station";
import { NieuwSmsBerichtDialog } from "@/features/berichten/components/nieuw-sms-bericht-dialog";
import { NieuwContactpersoon } from "@/features/contactpersonen";
import { ActionsDropdown, Checkbox, ConfirmDialog, DropdownMenuItem, Icon, Label } from "@/shared/components";
import { cn } from "@/shared/utils";
import { useState } from "react";
import { useParams } from "react-router";
import { MedewerkersStationBemenstIndicator, MedewerkersStationStatus, MedewerkersStationStatusIcon } from "../shared";

type MedewerkersStationProps = {
    station: MedewerkersOpStation;
    isSelected: boolean;
    onSelectionChange: (stationCode: string, isSelected: boolean) => void;
    onRequestVosSuccess: (stationCode: string) => void;
    onCreateMol: (stationCode: string) => void;
};

export function MedewerkersStation({ station, isSelected, onSelectionChange, onRequestVosSuccess, onCreateMol }: MedewerkersStationProps) {
    const { id: dossierNummer } = useParams() as { id: string };
    const getStationDisplayName = useGetStationDisplayName("withoutStationCode");
    const requestVossenMutation = usePostMedewerkersOpStationAanvraag();

    const stationCode = station.stationCode;
    const disabled = !station.veilig || station.aantalBeschikbareVossen === 0;
    const stationName = getStationDisplayName(stationCode);

    const handleRequestVos = () => {
        requestVossenMutation.mutate(
            { dossierId: dossierNummer, stationCodes: [stationCode] },
            {
                onSuccess: () => onRequestVosSuccess(stationCode),
            }
        );
    };

    return (
        <div className="flex p-4" data-testid={`vos-station-${stationCode}`}>
            <div className="flex flex-1 items-center gap-2">
                {requestVossenMutation.isPending && <Icon name="loader" size="xs" className="text-content-info animate-spin" />}
                {!requestVossenMutation.isPending &&
                    (station.status === "NietAangevraagd" ? (
                        <Checkbox
                            id={`check-${stationCode}`}
                            checked={isSelected}
                            onCheckedChange={(value) => {
                                onSelectionChange(stationCode, value === true);
                            }}
                            disabled={disabled}
                        />
                    ) : (
                        <MedewerkersStationStatusIcon station={station} />
                    ))}
                <Label
                    htmlFor={`check-${stationCode}`}
                    className={cn("text-content-default font-medium", station.status === "NietAangevraagd" && "cursor-pointer")}
                >
                    {stationName}
                </Label>
                <MedewerkersStationBemenstIndicator station={station} disabled={disabled} />
            </div>
            <MedewerkersStationStatus station={station} />

            <div className="flex basis-12 justify-end">
                {station.veilig && (
                    <MedewerkersStationActions
                        station={station}
                        name={stationName}
                        onRequestVos={handleRequestVos}
                        onCreateMol={() => onCreateMol(stationCode)}
                    />
                )}
            </div>
        </div>
    );
}

type MedewerkersStationActionsProps = {
    station: MedewerkersOpStation;
    name: string;
    onRequestVos: () => void;
    onCreateMol: () => void;
};

function MedewerkersStationActions({ station, name, onRequestVos, onCreateMol }: MedewerkersStationActionsProps) {
    const { id: dossierNummer } = useParams() as { id: string };
    const [stopVosInzetDialogOpen, setStopVosInzetDialogOpen] = useState(false);
    const [addMolDialogOpen, setAddMolDialogOpen] = useState(false);
    const [showNieuwSmsBerichtDialog, setShowNieuwSmsBerichtDialog] = useState(false);
    const patchMedewerkersOpStation = usePatchMedewerkersOpStation();

    const { status, stationCode, aantalBeschikbareVossen } = station;

    const handleStopInzet = () => {
        patchMedewerkersOpStation.mutate(
            {
                dossierNummer,
                actie: "Stop",
                scope: "Station",
                stationcode: stationCode,
            },
            {
                onSuccess: () => setStopVosInzetDialogOpen(false),
            }
        );
    };

    const contactIds = [...station.vossen, ...station.mollen].map((medewerker) => String(medewerker.contactpersoon.id));

    return (
        <>
            <ActionsDropdown className="col-start-3">
                {status === "NietAangevraagd" && aantalBeschikbareVossen > 0 && (
                    <DropdownMenuItem onSelect={onRequestVos}>Verstuur VOS aanvraag</DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setAddMolDialogOpen(true)}>MOL toevoegen</DropdownMenuItem>
                {(status === "AanvraagLoopt" || status === "VosIngezet") && (
                    <DropdownMenuItem onSelect={() => setStopVosInzetDialogOpen(true)}>Inzet VOS stoppen</DropdownMenuItem>
                )}
                {contactIds.length > 0 && (
                    <DropdownMenuItem onSelect={() => setShowNieuwSmsBerichtDialog(true)}>Bericht naar medewerkers sturen</DropdownMenuItem>
                )}
            </ActionsDropdown>
            {stopVosInzetDialogOpen && (
                <ConfirmDialog
                    isOpen={stopVosInzetDialogOpen}
                    onCancel={() => setStopVosInzetDialogOpen(false)}
                    onConfirm={handleStopInzet}
                    isPending={patchMedewerkersOpStation.isPending}
                    error={patchMedewerkersOpStation.error}
                    title={`VOS inzet ${name} stoppen`}
                    confirmLabel="Inzet stopzetten"
                    cancelLabel="Annuleren"
                    destructive
                >
                    Weet je zeker dat je alle VOS inzet wilt stopzetten? De VOS'en op {name} krijgen een sms-bericht dat inzet niet langer
                    nodig is.
                </ConfirmDialog>
            )}
            {addMolDialogOpen && (
                <NieuwContactpersoon
                    dossierId={dossierNummer}
                    functie="MOL"
                    stationcode={stationCode}
                    open={addMolDialogOpen}
                    onOpenChange={setAddMolDialogOpen}
                    onCreate={onCreateMol}
                />
            )}
            {showNieuwSmsBerichtDialog && (
                <NieuwSmsBerichtDialog
                    dossierId={dossierNummer}
                    open={showNieuwSmsBerichtDialog}
                    onOpenChange={setShowNieuwSmsBerichtDialog}
                    prefilledContactIds={contactIds}
                    allowCreateContactpersoon={false}
                />
            )}
        </>
    );
}
